import { useState, useRef } from 'react';
import { Navbar, Footer } from './LandingPage';
import { toast } from 'sonner';
import {
  Video,
  Users,
  Globe,
  Award,
  BookOpen,
  CheckCircle,
  Shield,
  Zap,
  Mail,
  Phone,
  Linkedin,
  Instagram,
  Facebook,
  Youtube,
  Share2
} from 'lucide-react';

const SupportPage = () => {
  const contactFormRef = useRef<HTMLDivElement>(null);
  const [searchQuery] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const faqs = [
    { category: "Getting Started", count: 12, icon: <Zap className="w-5 h-5" /> },
    { category: "Scanning", count: 15, icon: <BookOpen className="w-5 h-5" /> },
    { category: "Rubrics", count: 10, icon: <Award className="w-5 h-5" /> },
    { category: "Pricing & Billing", count: 8, icon: <Globe className="w-5 h-5" /> },
    { category: "Data Privacy", count: 6, icon: <Shield className="w-5 h-5" /> },
    { category: "Troubleshooting", count: 14, icon: <CheckCircle className="w-5 h-5" /> },
  ];

  const filteredFaqs = faqs.filter(faq =>
    faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBookTraining = () => {
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const validateEmail = (emailStr: string) => {
    // Industry standard email validation regex (RFC 5322 standard-compliant format checks)
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(emailStr);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    
    // Strictly block typing past character limits as character validation
    if (id === 'name' && value.length > 25) return;
    if (id === 'subject' && value.length > 25) return;
    if (id === 'message' && value.length > 250) return;

    setFormData(prev => ({
      ...prev,
      [id]: value
    }));

    // Clear error when the user starts typing to fix it
    setErrors(prev => ({
      ...prev,
      [id]: ''
    }));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id, value } = e.target;
    if (id === 'email' && value.trim()) {
      if (!validateEmail(value)) {
        setErrors(prev => ({
          ...prev,
          email: 'Please enter a valid email address'
        }));
      }
    }
  };

  const handleFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const newErrors = {
      name: '',
      email: '',
      subject: '',
      message: ''
    };

    let hasError = false;

    // Validate Name
    if (!formData.name.trim()) {
      newErrors.name = 'Full name is required';
      hasError = true;
    } else if (formData.name.length > 25) {
      newErrors.name = 'Name cannot exceed 25 characters';
      hasError = true;
    }

    // Validate Email
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
      hasError = true;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      hasError = true;
    }

    // Validate Subject
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
      hasError = true;
    } else if (formData.subject.length > 25) {
      newErrors.subject = 'Subject cannot exceed 25 characters';
      hasError = true;
    }

    // Validate Message
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
      hasError = true;
    } else if (formData.message.length > 250) {
      newErrors.message = 'Message cannot exceed 250 characters';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      toast.error('Please fix the validation errors in the form.');
      return;
    }

    try {
      const webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL;
      console.log('Sending to webhook:', formData);
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      toast.success('Message sent successfully!');
      setFormStatus('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
      setErrors({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setFormStatus(''), 3000);
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Failed to send message. Please try again.');
    }
  };





  return (
    <div className="bg-white min-h-screen font-sans">
      <Navbar />

      {/* Hero Section with Search */}
      <section className="pt-20 sm:pt-32 pb-16 sm:pb-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[linear-gradient(to_right,#444_1px,transparent_1px),linear-gradient(to_bottom,#444_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500 rounded-full blur-[100px] opacity-20" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 text-center">
          <span className="text-xs sm:text-sm font-bold uppercase tracking-widest text-gray-400 bg-white/10 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full inline-block">Support Center</span>
          <h1 className="text-3xl sm:text-5xl md:text-6xl mt-4 sm:mt-6 mb-3 sm:mb-4 bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent leading-[1.1] tracking-[-0.02em]">
            How can we help you today?
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-400 mb-8 sm:mb-10 max-w-2xl mx-auto px-2">
            Search our knowledge base or get in touch with our team.
          </p>

          {/* Search Bar - Commented Out */}
          {/* <div className="max-w-2xl mx-auto relative px-4 sm:px-0">
            <Search className="absolute left-8 sm:left-5 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Getting Started, Scanning, Rubrics, Pricing, Privacy, Troubleshooting..."
              className="w-full pl-14 pr-6 py-4 bg-white/10 border border-white/20 rounded-2xl text-white placeholder-gray-300 focus:outline-none focus:border-white transition-colors text-base sm:text-lg focus:placeholder-gray-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div> */}
        </div>
      </section>



      {/* Contact Information Section */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-8 md:mb-16">
            <span className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full inline-block">Contact Information</span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mt-3 sm:mt-4 mb-2 sm:mb-4">Get in Touch</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto font-medium px-2">Multiple ways to reach our team for support and inquiries.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {/* Email Contacts */}
            <div className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-[24px] md:rounded-[32px] border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col items-center text-center">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-blue-50 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 text-blue-600">
                <Mail className="w-5 sm:w-6 h-5 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black mb-3 sm:mb-4">Email</h3>
              <div className="space-y-2 sm:space-y-4 w-full">
                <div className="bg-white/60 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-gray-100">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">General Inquiries</p>
                  <a href="mailto:info@kuberya.com" className="text-sm sm:text-base font-bold text-black hover:text-blue-600 transition-colors break-all">info@kuberya.com</a>
                </div>
                <div className="bg-white/60 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-gray-100">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">HR & Recruitment</p>
                  <a href="mailto:hr@kuberya.com" className="text-sm sm:text-base font-bold text-black hover:text-blue-600 transition-colors break-all">hr@kuberya.com</a>
                </div>
              </div>
            </div>

            {/* Phone & WhatsApp */}
            <div className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-[24px] md:rounded-[32px] border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col items-center text-center">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-green-50 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 text-green-600">
                <Phone className="w-5 sm:w-6 h-5 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black mb-3 sm:mb-4">Phone & WhatsApp</h3>
              <div className="space-y-2 sm:space-y-4 w-full">
                <div className="bg-white/60 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-gray-100">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Direct Contact</p>
                  <a href="tel:+918989640004" className="text-sm sm:text-base font-bold text-black hover:text-green-600 transition-colors inline-flex items-center gap-1 sm:gap-1.5 justify-center flex-wrap">
                    <Phone className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>+91 89896 40004</span>
                  </a>
                  <p className="text-[10px] text-gray-400 mt-1 font-medium">Mon-Fri 9am-6pm IST</p>
                </div>
                <div className="bg-white/60 p-3 sm:p-4 rounded-xl sm:rounded-2xl border border-gray-100">
                  <p className="text-[10px] sm:text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">WhatsApp Support</p>
                  <a href="https://wa.me/918989640004" target="_blank" rel="noopener noreferrer" className="text-sm sm:text-base font-bold text-black hover:text-green-600 transition-colors inline-flex items-center gap-1 sm:gap-1.5 justify-center flex-wrap">
                    <svg className="w-4 h-4 text-green-600 fill-current flex-shrink-0" viewBox="0 0 24 24">
                      <path d="M12.031 2c-5.51 0-9.989 4.478-9.989 9.989 0 1.761.458 3.473 1.332 4.98L2 22l5.163-1.355c1.464.798 3.111 1.218 4.868 1.22h.004c5.509 0 9.99-4.478 9.99-9.99A9.92 9.92 0 0012.031 2zm6.657 14.15c-.273.76-1.583 1.39-2.185 1.45-.54.06-1.08.31-3.46-.63-3.04-1.2-4.99-4.3-5.14-4.5-.15-.2-1.2-1.59-1.2-3.03 0-1.44.75-2.15 1.02-2.44.2-.21.5-.32.8-.32.1 0 .2 0 .3.02.27.01.41.03.59.45.22.52.75 1.83.82 1.97.07.15.12.32.02.5-.1.18-.15.3-.3.47-.15.18-.32.4-.45.54-.15.15-.3.32-.13.62.3.52.75.95 1.25 1.39.65.57 1.33.94 1.88 1.21.3.15.48.12.66-.08.2-.23.87-1.01 1.1-1.35.08-.12.23-.2.38-.15.15.05 1.02.48 1.2.57.18.09.3.14.35.21.05.08.05.45-.22 1.21z" />
                    </svg>
                    <span>+91 89896 40004</span>
                  </a>
                  <p className="text-[10px] text-gray-400 mt-1 font-medium">Mon-Fri 9am-6pm IST</p>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div className="bg-gradient-to-br from-gray-50 to-white p-4 sm:p-6 md:p-8 rounded-2xl sm:rounded-[24px] md:rounded-[32px] border border-gray-100 shadow-sm hover:shadow-md transition-all flex flex-col items-center text-center">
              <div className="w-10 sm:w-12 h-10 sm:h-12 bg-purple-50 rounded-2xl flex items-center justify-center mb-3 sm:mb-4 text-purple-600">
                <Share2 className="w-5 sm:w-6 h-5 sm:h-6" />
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-black mb-3 sm:mb-4">Follow Us</h3>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 w-full">
                <a
                  href="https://www.linkedin.com/company/kuberya-ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 bg-white hover:bg-blue-50/50 rounded-lg sm:rounded-2xl border border-gray-100 hover:border-blue-200 transition-all group"
                >
                  <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl bg-blue-50 flex items-center justify-center text-blue-600 group-hover:scale-110 transition-transform">
                    <Linkedin className="w-4 sm:w-5 h-4 sm:h-5 fill-current" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-bold text-gray-700 group-hover:text-blue-700 transition-colors">LinkedIn</span>
                </a>

                <a
                  href="https://www.instagram.com/kuberyaai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 bg-white hover:bg-pink-50/50 rounded-lg sm:rounded-2xl border border-gray-100 hover:border-pink-200 transition-all group"
                >
                  <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl bg-pink-50 flex items-center justify-center text-pink-600 group-hover:scale-110 transition-transform">
                    <Instagram className="w-4 sm:w-5 h-4 sm:h-5" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-bold text-gray-700 group-hover:text-pink-700 transition-colors">Instagram</span>
                </a>

                <a
                  href="https://www.facebook.com/profile.php?id=61575507550246"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 bg-white hover:bg-blue-50/50 rounded-lg sm:rounded-2xl border border-gray-100 hover:border-blue-300 transition-all group"
                >
                  <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl bg-blue-100/50 flex items-center justify-center text-blue-700 group-hover:scale-110 transition-transform">
                    <Facebook className="w-4 sm:w-5 h-4 sm:h-5 fill-current" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-bold text-gray-700 group-hover:text-blue-800 transition-colors">Facebook</span>
                </a>

                <a
                  href="https://www.youtube.com/@kuberya-AI"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center gap-1 sm:gap-2 p-2 sm:p-4 bg-white hover:bg-red-50/50 rounded-lg sm:rounded-2xl border border-gray-100 hover:border-red-200 transition-all group"
                >
                  <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-lg sm:rounded-xl bg-red-50 flex items-center justify-center text-red-600 group-hover:scale-110 transition-transform">
                    <Youtube className="w-4 sm:w-5 h-4 sm:h-5" />
                  </div>
                  <span className="text-[10px] sm:text-xs font-bold text-gray-700 group-hover:text-red-700 transition-colors">YouTube</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-8 sm:py-12 md:py-24 bg-gray-50" ref={contactFormRef}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-white p-4 sm:p-8 md:p-12 rounded-2xl sm:rounded-[24px] md:rounded-[40px] border border-gray-100 shadow-sm">
            <div className="text-center mb-6 sm:mb-8 md:mb-12">
              <span className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full inline-block">Contact Form</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mt-3 sm:mt-4 mb-2">Send us a Message</h2>
              <p className="text-sm sm:text-base text-gray-500 font-medium px-2">We usually respond within 1 business day.</p>
            </div>

            <form className="space-y-4 sm:space-y-6" onSubmit={handleFormSubmit}>
              {formStatus && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-medium">
                  {formStatus}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="name" className="block text-xs sm:text-sm font-bold text-slate-900">Full Name</label>
                    <span className={`text-xs font-semibold transition-colors ${
                      formData.name.length >= 25 ? 'text-red-500' : 'text-gray-400'
                    }`}>
                      {formData.name.length}/25
                    </span>
                  </div>
                  <input 
                    type="text" 
                    id="name" 
                    placeholder="John Doe" 
                    value={formData.name} 
                    onChange={handleFormChange} 
                    maxLength={25}
                    required 
                    className={`w-full px-4 py-3 rounded-xl border outline-none transition-all font-medium text-sm ${
                      errors.name 
                        ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50/10' 
                        : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-black'
                    }`} 
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1.5 font-semibold transition-all duration-300">
                      {errors.name}
                    </p>
                  )}
                </div>
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label htmlFor="email" className="block text-sm font-bold text-slate-900">Email Address</label>
                  </div>
                  <input 
                    type="email" 
                    id="email" 
                    placeholder="john@example.com" 
                    value={formData.email} 
                    onChange={handleFormChange} 
                    onBlur={handleBlur}
                    required 
                    className={`w-full px-4 py-3 rounded-xl border outline-none transition-all font-medium text-sm ${
                      errors.email 
                        ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50/10' 
                        : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-black'
                    }`} 
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1.5 font-semibold transition-all duration-300">
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="subject" className="block text-sm font-bold text-slate-900">Subject</label>
                  <span className={`text-xs font-semibold transition-colors ${
                    formData.subject.length >= 25 ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    {formData.subject.length}/25
                  </span>
                </div>
                <input 
                  type="text" 
                  id="subject" 
                  placeholder="How can we help you?" 
                  value={formData.subject} 
                  onChange={handleFormChange} 
                  maxLength={25}
                  required 
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition-all font-medium text-sm ${
                    errors.subject 
                      ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50/10' 
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-black'
                  }`} 
                />
                {errors.subject && (
                  <p className="text-red-500 text-xs mt-1.5 font-semibold transition-all duration-300">
                    {errors.subject}
                  </p>
                )}
              </div>
              <div>
                <div className="flex justify-between items-center mb-2">
                  <label htmlFor="message" className="block text-sm font-bold text-slate-900">Message</label>
                  <span className={`text-xs font-semibold transition-colors ${
                    formData.message.length >= 250 ? 'text-red-500' : 'text-gray-400'
                  }`}>
                    {formData.message.length}/250
                  </span>
                </div>
                <textarea 
                  id="message" 
                  rows={5} 
                  placeholder="Type your message here..." 
                  value={formData.message} 
                  onChange={handleFormChange} 
                  maxLength={250}
                  required 
                  className={`w-full px-4 py-3 rounded-xl border outline-none transition-all font-medium text-sm resize-none ${
                    errors.message 
                      ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500 bg-red-50/10' 
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-black'
                  }`}
                />
                {errors.message && (
                  <p className="text-red-500 text-xs mt-1.5 font-semibold transition-all duration-300">
                    {errors.message}
                  </p>
                )}
              </div>
              <div>
                <button type="submit" className="w-full bg-black text-white font-bold py-4 rounded-xl hover:bg-gray-800 transition-colors text-sm uppercase tracking-wider">Send Message</button>
              </div>
            </form>
          </div>
        </div>
      </section>

      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Framed Container */}
          <div className="bg-gray-50 p-4 sm:p-8 md:p-12 lg:p-16 rounded-2xl sm:rounded-[24px] md:rounded-[40px] border border-gray-100">

            {/* Heading inside the container */}
            <div className="text-center mb-6 sm:mb-8 md:mb-16">
              <span className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-widest bg-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-sm inline-block">Knowledge Base</span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mt-3 sm:mt-4">Browse by Category</h2>
              <p className="text-sm sm:text-base text-gray-600 mt-2 font-medium px-2">50+ articles seeded at launch to help you succeed.</p>
            </div>

            {/* Grid of white cards */}
            {filteredFaqs.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                {filteredFaqs.map((item, index) => (
                  <div key={index} className="bg-white p-4 sm:p-6 md:p-10 rounded-xl sm:rounded-[20px] md:rounded-[32px] border border-gray-50 hover:shadow-xs transition-all duration-300 transform hover:-translate-y-1 cursor-pointer flex flex-col justify-between group">
                    <div>
                      <div className="text-5xl font-bold text-gray-100 mb-6 group-hover:text-black/10 transition-colors">
                        {index < 9 ? `0${index + 1}` : index + 1}
                      </div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{item.category}</h3>
                      <p className="text-sm text-slate-500 font-medium">{item.count} articles</p>
                    </div>
                    <div className="mt-8 flex items-center gap-1 text-sm font-bold text-black">
                      <span>View Articles</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg font-medium">No categories found matching "{searchQuery}"</p>
              </div>
            )}

          </div>
          
        </div>
      </section>


      {/* 6. Faculty training & enablement */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">

          {/* Framed Container */}
          <div className="bg-gray-50 p-4 sm:p-8 md:p-12 lg:p-16 rounded-2xl sm:rounded-[24px] md:rounded-[40px] border border-gray-100">

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">

              <div className="lg:col-span-5">
                <span className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-widest bg-white px-3 sm:px-4 py-1 sm:py-1.5 rounded-full shadow-sm inline-block">Enablement</span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mt-3 sm:mt-4 mb-3 sm:mb-4">Faculty Training & Enablement</h2>
                <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 font-medium">Free 60-minute live training sessions every Friday. Custom training for institutions with 10+ teachers.</p>

                <button onClick={handleBookTraining} className="bg-black text-white font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-lg sm:rounded-xl hover:bg-gray-800 transition-colors flex items-center gap-2 text-sm sm:text-base">
                  Book Training Session
                </button>
              </div>

              <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 md:gap-6">
                <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-2xl border border-gray-100 hover:border-black hover:shadow-lg transition-all transform hover:-translate-y-1 cursor-pointer group">
                  <div className="w-8 sm:w-10 h-8 sm:h-10 bg-gray-50 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-black group-hover:text-white transition-colors">
                    <Video className="w-4 sm:w-5 h-4 sm:h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base mb-1 sm:mb-2">Weekly Live Training</h3>
                  <p className="text-xs sm:text-sm text-slate-500">Every Friday. Open to all faculty members.</p>
                </div>

                <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-2xl border border-gray-100 hover:border-black hover:shadow-lg transition-all transform hover:-translate-y-1 cursor-pointer group">
                  <div className="w-8 sm:w-10 h-8 sm:h-10 bg-gray-50 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-black group-hover:text-white transition-colors">
                    <BookOpen className="w-4 sm:w-5 h-4 sm:h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base mb-1 sm:mb-2">Recorded Library</h3>
                  <p className="text-xs sm:text-sm text-slate-500">Access all past training sessions on-demand.</p>
                </div>

                <div className="bg-white p-4 sm:p-6 rounded-lg sm:rounded-2xl border border-gray-100 hover:border-black hover:shadow-lg transition-all transform hover:-translate-y-1 cursor-pointer group sm:col-span-2">
                  <div className="w-8 sm:w-10 h-8 sm:h-10 bg-gray-50 rounded-lg sm:rounded-xl flex items-center justify-center mb-3 sm:mb-4 group-hover:bg-black group-hover:text-white transition-colors">
                    <Users className="w-4 sm:w-5 h-4 sm:h-5" />
                  </div>
                  <h3 className="font-bold text-slate-900 text-sm sm:text-base mb-1 sm:mb-2">Custom Institution Training</h3>
                  <p className="text-xs sm:text-sm text-slate-500">For institutions with 10+ teachers. Tailored to your specific needs.</p>
                </div>
              </div>

            </div>

          </div>
          
        </div>
      </section>

      {/* 7. Community & resources */}
      <section className="py-8 sm:py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <span className="text-xs sm:text-sm font-bold text-slate-900 uppercase tracking-widest bg-gray-100 px-3 sm:px-4 py-1 sm:py-1.5 rounded-full inline-block">Community</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-black mt-3 sm:mt-4 mb-3 sm:mb-4">Stay Connected</h2>
          <p className="text-sm sm:text-base text-gray-600 max-w-2xl mx-auto mb-6 sm:mb-8 md:mb-12 font-medium px-2">Join our community of power users and stay updated with the latest in AI evaluation.</p>

          <div className="bg-black p-4 sm:p-8 md:p-12 lg:p-16 rounded-2xl sm:rounded-[24px] md:rounded-[40px] text-white overflow-hidden relative max-w-5xl mx-auto mt-6 sm:mt-12 border border-gray-800">
            {/* Glow effect */}
            <div className="absolute -top-20 -left-20 w-60 h-60 bg-blue-600 rounded-full blur-[80px] opacity-20" />

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8 md:divide-x divide-gray-800 relative z-10">
              
              {/* Newsletter */}
              <a href="mailto:info@kuberya.com?subject=Subscribe%20to%20Newsletter" className="flex flex-col justify-between group cursor-pointer text-center px-3 sm:px-6 hover:opacity-80 transition-opacity">
                <div>
                  <div className="w-10 sm:w-14 h-10 sm:h-14 mb-3 sm:mb-6 overflow-hidden mx-auto rounded-lg sm:rounded-2xl bg-white/5 p-2 group-hover:scale-110 transition-transform duration-500">
                    <img src="/gmail-logo-gmail-logo-for-email-platform-uNTUyFDX_t-removebg-preview.png" alt="Email" className="w-full h-full object-contain" />
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2 text-white">Newsletter</h3>
                  <p className="text-gray-400 text-xs sm:text-sm font-medium mb-2 sm:mb-4">Sign up for regular updates and tips.</p>
                </div>
                <div className="text-xs sm:text-sm font-bold text-white mt-2 sm:mt-4">Subscribe</div>
              </a>

              {/* LinkedIn */}
              <a href="https://www.linkedin.com/company/kuberya-ai/" target="_blank" rel="noopener noreferrer" className="flex flex-col justify-between group cursor-pointer text-center px-3 sm:px-6 hover:opacity-80 transition-opacity">
                <div>
                  <div className="w-10 sm:w-14 h-10 sm:h-14 mb-3 sm:mb-6 overflow-hidden mx-auto rounded-lg sm:rounded-2xl bg-white/5 p-2 group-hover:scale-110 transition-transform duration-500">
                    <img src="/png-clipart-linkedin-linkedin-thumbnail-removebg-preview.png" alt="LinkedIn" className="w-full h-full object-contain" />
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2 text-white">LinkedIn</h3>
                  <p className="text-gray-400 text-xs sm:text-sm font-medium mb-2 sm:mb-4">Connect with Aniruddha & the company.</p>
                </div>
                <div className="text-xs sm:text-sm font-bold text-white mt-2 sm:mt-4">Follow</div>
              </a>

              {/* WhatsApp */}
              <a href="https://wa.me/918989640004" target="_blank" rel="noopener noreferrer" className="flex flex-col justify-between group cursor-pointer text-center px-3 sm:px-6 hover:opacity-80 transition-opacity">
                <div>
                  <div className="w-10 sm:w-14 h-10 sm:h-14 mb-3 sm:mb-6 overflow-hidden mx-auto rounded-lg sm:rounded-2xl bg-white/5 p-2 group-hover:scale-110 transition-transform duration-500">
                    <img src="/png-clipart-whats-app-logo-whatsapp-logo-whatsapp-cdr-leaf-thumbnail.png" alt="WhatsApp" className="w-full h-full object-contain" />
                  </div>
                  <h3 className="font-bold text-base sm:text-lg mb-1 sm:mb-2 text-white">Educator Community</h3>
                  <p className="text-gray-400 text-xs sm:text-sm font-medium mb-2 sm:mb-4">Join our Slack/WhatsApp group.</p>
                </div>
                <div className="text-xs sm:text-sm font-bold text-white mt-2 sm:mt-4">Join Group</div>
              </a>

            </div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
};

export default SupportPage;
